import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { startCheckout } from "@/pages/PaymentPages";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/lib/i18n";
import DocumentDrop from "@/components/DocumentDrop";
import ScanHistory from "@/components/ScanHistory";
import { Sparkles, ArrowRight, ScanLine } from "lucide-react";

export default function DocumentScanPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useLang();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!file) return;
    setLoading(true);
    try {
      toast.loading(user?.plan === "surveillance" ? t("doc.analyzing") : t("pay.redirect"), { id: "doc" });
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/scan/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.requires_payment) {
        await startCheckout("scan_full", null, { upload_ref: data.upload_ref, upload_kind: data.upload_kind });
      } else {
        toast.success(t("pay.done"), { id: "doc" });
        nav(`/app/scan/${data.scan_id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("scan.error"), { id: "doc" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="document-scan-page">
      <div>
        <p className="eyebrow mb-1">{t("doc.eyebrow")}</p>
        <h1 className="text-3xl sm:text-4xl font-serif tracking-tight">{t("doc.title")}</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl leading-relaxed">{t("doc.sub")}</p>
      </div>

      <DocumentDrop onFile={setFile} file={file} disabled={loading} testid="app-document-drop" hint={t("doc.drop.hint")} />

      <div className="flex flex-col sm:flex-row items-center gap-4">
        <Button onClick={run} disabled={loading || !file}
          className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-7 font-semibold w-full sm:w-auto"
          data-testid="document-scan-submit">
          <ScanLine className="w-4 h-4 mr-2" /> {loading ? "…" : t("doc.cta")} <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
        <p className="font-mono text-xs text-muted-foreground">
          {user?.plan === "surveillance" ? t("doc.included") : t("doc.paid")}
        </p>
      </div>

      <div className="hairline p-5 rounded-lg flex items-start gap-3 bg-accent/40" data-testid="document-scan-note">
        <Sparkles className="w-4 h-4 text-primary mt-0.5 shrink-0" />
        <p className="text-sm text-muted-foreground leading-relaxed">{t("doc.note")}</p>
      </div>

      <ScanHistory source="document" title="Historique — Analyses document" />
    </div>
  );
}
